import * as moment from 'moment';
import * as React from 'react';
import { Query } from 'react-apollo';
import Helmet from 'react-helmet-async';

import { withStyles } from '@gdp/react-app/lib/helpers/withStyles';
import { HasAnyAuthority } from 'src/app/components/HasAnyAuthority';
import { withAuth } from 'src/app/components/WithAuth';
import { MainPage } from 'src/app/views/MainPage';
import { BranchPerformanceSection } from './BranchPerformanceSection';
import { BusinessHealthSection } from './BusinessHealthSection';
import { DASHBOARD_DATE_FORMAT } from './Dashboard.constants';
import {
  BranchPerformanceMetric,
  DashboardFilterInput,
  DashboardTrendGranularity,
  FETCH_DASHBOARD_OVERVIEW_QUERY,
  FetchDashboardOverviewQueryResult
} from './Dashboard.queries';
import { DashboardFilterBar } from './DashboardFilterBar';
import { TodaySnapshotCard } from './TodaySnapshotCard';
import { TrendSection } from './TrendSection';

export const Dashboard = () => withAuth({ Component: DashboardContainer, isAuthenticated: true });

interface DashboardState {
  branchId?: number;
  branchMetric: BranchPerformanceMetric;
  endDate: string;
  granularity: DashboardTrendGranularity;
  startDate: string;
}

const VIEW_ORDER_AUTHORITY = 'VIEW_ORDER';

/**
 * Opens on the current month to date, the range the dashboard has always shown first — admins who
 * only want the monthly view see no change until they touch the pickers.
 */
const resolveInitialRange = (): { startDate: string; endDate: string } => {
  const today = moment();

  return {
    startDate: today
      .clone()
      .startOf('month')
      .format(DASHBOARD_DATE_FORMAT),
    endDate: today.format(DASHBOARD_DATE_FORMAT)
  };
};

@withStyles(require('./Dashboard.scss'))
class DashboardContainer extends React.Component<{}, DashboardState> {
  constructor(props: {}) {
    super(props);

    const { startDate, endDate } = resolveInitialRange();
    this.state = {
      branchId: undefined,
      branchMetric: BranchPerformanceMetric.REVENUE,
      endDate,
      granularity: DashboardTrendGranularity.DAILY,
      startDate
    };
  }

  render() {
    const { branchId, branchMetric, endDate, granularity, startDate } = this.state;
    const filterInput: DashboardFilterInput = { startDate, endDate, branchId };

    return (
      <MainPage pageTitle="link.dashboard">
        <Helmet>
          <title>Dashboard</title>
        </Helmet>

        <div className="Dashboard">
          <p className="Dashboard__subtitle">Overview of Rumah OASIS performance</p>

          <DashboardFilterBar
            branchId={branchId}
            endDate={endDate}
            onBranchChange={this.handleBranchChange}
            onRangeChange={this.handleRangeChange}
            startDate={startDate}
          />

          <HasAnyAuthority authorities={[VIEW_ORDER_AUTHORITY]}>
            <Query<FetchDashboardOverviewQueryResult>
              query={FETCH_DASHBOARD_OVERVIEW_QUERY}
              variables={{ input: filterInput }}
            >
              {({ loading, error, data }) => {
                if (loading) return <div className="Dashboard__loading">Loading dashboard...</div>;
                if (error) return <div className="Dashboard__error">Failed to load dashboard</div>;
                if (!data || !data.dashboardOverview)
                  return <div className="Dashboard__empty">No dashboard data available</div>;

                const { businessHealth, branchPerformance, generatedAt } = data.dashboardOverview;

                return (
                  <div>
                    {/* Sits directly under the filter: it qualifies every figure on the page. */}
                    <div className="Dashboard__generated-at">
                      Data as of {moment(generatedAt).format('DD MMM YYYY HH:mm')}
                    </div>

                    <h2 className="Dashboard__section-title">1. Business Health</h2>
                    <BusinessHealthSection
                      businessHealth={businessHealth}
                      comparisonLabel={this.getComparisonLabel()}
                    />

                    <h2 className="Dashboard__section-title">2. Branch Performance</h2>
                    <BranchPerformanceSection
                      branchPerformance={branchPerformance}
                      metric={branchMetric}
                      onMetricChange={this.handleBranchMetricChange}
                    />
                  </div>
                );
              }}
            </Query>

            <h2 className="Dashboard__section-title">3. Trend &amp; Operational Snapshot</h2>
            <div className="Dashboard__grid Dashboard__grid--trend">
              <TrendSection
                filterInput={filterInput}
                granularity={granularity}
                onGranularityChange={this.handleGranularityChange}
              />
              <TodaySnapshotCard branchId={branchId} />
            </div>
          </HasAnyAuthority>
        </div>
      </MainPage>
    );
  }

  /**
   * Mirrors resolveShiftedRange on the backend: the window of the same length sitting immediately
   * before the selected one. 1–14 Sep reads "vs 18–31 Aug".
   *
   * The two must stay in step — this label is the only place an admin can see which period the
   * deltas were measured against, so a mismatch here misreports every percentage on the page.
   */
  private readonly getComparisonLabel = (): string => {
    const { startDate, endDate } = this.state;
    const start = moment(startDate, DASHBOARD_DATE_FORMAT);
    const end = moment(endDate, DASHBOARD_DATE_FORMAT);
    const lengthInDays = end.diff(start, 'days') + 1;

    const previousStart = start.clone().subtract(lengthInDays, 'days');
    const previousEnd = end.clone().subtract(lengthInDays, 'days');

    return `vs ${previousStart.format('D MMM')} – ${previousEnd.format('D MMM YYYY')}`;
  };

  private readonly handleRangeChange = (startDate: string, endDate: string) => {
    this.setState({ startDate, endDate });
  };

  private readonly handleBranchChange = (branchId?: number) => {
    this.setState({ branchId });
  };

  private readonly handleBranchMetricChange = (branchMetric: BranchPerformanceMetric) => {
    this.setState({ branchMetric });
  };

  private readonly handleGranularityChange = (granularity: DashboardTrendGranularity) => {
    this.setState({ granularity });
  };
}
